import React, { useState } from "react";
import {
  Modal,
  Group,
  Button,
  Text,
  Image,
  Stack,
  Badge,
  Divider,
  Box,
  Grid,
  ActionIcon,
  Menu,
  Title,
  Table,
} from "@mantine/core";
import {
  IconEdit,
  IconDotsVertical,
  IconPackage,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { useProducts } from "../../context/ProductsContext";
import { useApp } from "../../context/AppContext";
import { useFeatureAccess } from "../../context/FeatureAccessContext";
import { Product } from "../../types/products";
import { ProductQuantityAdjustment } from "../../types/productQuantityAdjustments";
import EditProductModal from "./EditProductModal";
import AdjustQuantityModal from "./AdjustQuantityModal";
import EditQuantityAdjustmentModal from "./EditQuantityAdjustmentModal";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";
import PageHeaderBar from "../common/PageHeaderBar";
import ProductTaxSummary from "./ProductTaxSummary";
import { useProductTaxRates } from "../../hooks/useProductTaxRates";

interface ProductDetailModalProps {
  opened: boolean;
  onClose: () => void;
  product: Product | null;
}

export default function ProductDetailModal({
  opened,
  onClose,
  product,
}: ProductDetailModalProps) {
  const { updateProduct, fetchProducts } = useProducts();
  const { deleteProductStockAdjustment } = useApp();
  const { access } = useFeatureAccess();
  const perms = access?.section_permissions?.products;
  const [editModalOpened, setEditModalOpened] = useState(false);
  const [adjustQuantityModalOpened, setAdjustQuantityModalOpened] =
    useState(false);
  const [editAdjustmentModalOpened, setEditAdjustmentModalOpened] =
    useState(false);
  const [selectedAdjustment, setSelectedAdjustment] =
    useState<ProductQuantityAdjustment | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [adjustmentToDelete, setAdjustmentToDelete] = useState<string | null>(
    null,
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const productTaxRates = useProductTaxRates(product?.id, opened);

  // Use stock_adjustments from product prop (now included in API response)
  const adjustments = product?.stock_adjustments || [];

  if (!product) {
    return null;
  }

  const handleRefreshAdjustments = async () => {
    // Refetch all products to get updated stock_adjustments
    await fetchProducts();
  };

  const handleDeleteAdjustment = (adjustmentId: string) => {
    setAdjustmentToDelete(adjustmentId);
    setDeleteModalOpened(true);
  };

  const confirmDeleteAdjustment = async () => {
    if (!adjustmentToDelete) return;

    try {
      setIsDeleting(true);
      await deleteProductStockAdjustment(adjustmentToDelete);
      await handleRefreshAdjustments();
      setDeleteModalOpened(false);
      setAdjustmentToDelete(null);
    } catch (error) {
      console.error("Error deleting stock adjustment:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditAdjustment = (adjustment: ProductQuantityAdjustment) => {
    setSelectedAdjustment(adjustment);
    setEditAdjustmentModalOpened(true);
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      setIsUpdatingStatus(true);
      await updateProduct(product.id, { status: newStatus });
    } catch (error) {
      console.error("Error updating product status:", error);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Active":
        return "green";
      case "Inactive":
        return "gray";
      case "Archive":
        return "red";
      default:
        return "blue";
    }
  };

  const getStockColor = (quantity: number, threshold: number) => {
    if (quantity <= threshold) {
      return "red";
    }
    return "green";
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        size="100%"
        padding={0}
        withCloseButton={false}
        styles={{
          content: { maxHeight: "100dvh", overflow: "auto" },
          body: { padding: 0, height: "100dvh", maxHeight: "100dvh" },
          inner: { padding: 0 },
        }}
      >
        <Box h="100dvh" style={{ display: "flex", flexDirection: "column" }}>
          <PageHeaderBar label="All Products" onBack={onClose} />
          {/* Header */}
          <Box
            p="lg"
            style={{
              borderBottom: "1px solid var(--mantine-color-gray-3)",
              backgroundColor: "var(--mantine-color-gray-0)",
            }}
          >
            <Group justify="space-between" align="center">
              <Group>
                <Title order={2}>{product.name}</Title>
              </Group>

              <Group>
                {perms?.canEdit !== false && (
                  <Button
                    leftSection={<IconPlus size={14} />}
                    onClick={() => setAdjustQuantityModalOpened(true)}
                  >
                    Adjust Quantity
                  </Button>
                )}
                {perms?.canEdit !== false && (
                  <Button
                    leftSection={<IconEdit size={16} />}
                    onClick={() => setEditModalOpened(true)}
                  >
                    Edit Product
                  </Button>
                )}

                {perms?.canEdit !== false && (
                  <Menu>
                    <Menu.Target>
                      <ActionIcon variant="subtle" size="lg">
                        <IconDotsVertical size={16} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Label>Status</Menu.Label>
                      <Menu.Item
                        onClick={() => handleStatusChange("Active")}
                        disabled={
                          product.status === "Active" || isUpdatingStatus
                        }
                      >
                        Mark as Active
                      </Menu.Item>
                      <Menu.Item
                        onClick={() => handleStatusChange("Inactive")}
                        disabled={
                          product.status === "Inactive" || isUpdatingStatus
                        }
                      >
                        Mark as Inactive
                      </Menu.Item>
                      <Menu.Item
                        onClick={() => handleStatusChange("Archive")}
                        disabled={
                          product.status === "Archive" || isUpdatingStatus
                        }
                        color="red"
                      >
                        Archive Product
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                )}
              </Group>
            </Group>

            <Group mt="xs">
              <Badge color={getStatusColor(product.status)} variant="light">
                {product.status}
              </Badge>
              <Badge variant="light">{product.category}</Badge>
              <Badge variant="light">{product.company}</Badge>
            </Group>
          </Box>

          {/* Content */}
          <Box p="lg" style={{ flex: 1, overflow: "auto" }}>
            <Grid gutter="lg">
              {/* Left Column - Product Image and Basic Info */}
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Stack gap="lg">
                  <Box>
                    <Image
                      src={
                        product.img_url ||
                        "https://placehold.co/400x400?text=No+Image"
                      }
                      alt={`${product.name} product image`}
                      fallbackSrc="https://placehold.co/400x400?text=No+Image"
                      w="100%"
                      h={300}
                      fit="contain"
                      style={{
                        border: "1px solid var(--mantine-color-gray-3)",
                        borderRadius: "8px",
                      }}
                    />
                  </Box>

                  <Box>
                    <Text size="sm" fw={500} mb="xs">
                      Basic Information
                    </Text>
                    <Stack gap="xs">
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          Barcode:
                        </Text>
                        <Text size="sm" fw={500}>
                          {product.custom_id}
                        </Text>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          Unit Price:
                        </Text>
                        <Text size="sm" fw={500}>
                          {formatCurrency(product.list_price)}
                        </Text>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          Purchase Price:
                        </Text>
                        <Text size="sm" fw={500}>
                          {formatCurrency(product.purchase_price)}
                        </Text>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          Stock Status:
                        </Text>
                        <Badge
                          color={getStockColor(
                            product.quantity,
                            product.reorder_threshold,
                          )}
                        >
                          {product.quantity <= product.reorder_threshold
                            ? "Low Stock"
                            : "In Stock"}
                        </Badge>
                      </Group>
                    </Stack>
                  </Box>
                </Stack>
              </Grid.Col>

              {/* Right Column - Detailed Information */}
              <Grid.Col span={{ base: 12, md: 8 }}>
                <Stack gap="lg">
                  {/* Current Stock */}
                  <Box
                    p="md"
                    style={{
                      border: "1px solid var(--mantine-color-gray-3)",
                      borderRadius: "8px",
                      backgroundColor: "var(--mantine-color-gray-0)",
                    }}
                  >
                    <Text fw={500}>Current Stock</Text>
                    <Text
                      size="xl"
                      fw={700}
                      c={getStockColor(
                        product.quantity,
                        product.reorder_threshold,
                      )}
                    >
                      {product.quantity} units
                    </Text>
                    <Text size="sm" c="dimmed">
                      Reorder threshold: {product.reorder_threshold} units
                    </Text>
                  </Box>

                  {/* Product Details */}
                  <Box
                    p="md"
                    style={{
                      border: "1px solid var(--mantine-color-gray-3)",
                      borderRadius: "8px",
                      backgroundColor: "var(--mantine-color-gray-0)",
                    }}
                  >
                    <Text fw={500} mb="xs">
                      Product Details
                    </Text>
                    <Grid gutter="md">
                      <Grid.Col span={6}>
                        <Text size="sm" c="dimmed">
                          Category
                        </Text>
                        <Text size="sm" fw={500}>
                          {product.category}
                        </Text>
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <Text size="sm" c="dimmed">
                          Brand
                        </Text>
                        <Text size="sm" fw={500}>
                          {product.company}
                        </Text>
                      </Grid.Col>
                    </Grid>
                  </Box>

                  {/* Pricing Information */}
                  <Box
                    p="md"
                    style={{
                      border: "1px solid var(--mantine-color-gray-3)",
                      borderRadius: "8px",
                      backgroundColor: "var(--mantine-color-gray-0)",
                    }}
                  >
                    <Text fw={500} mb="xs">
                      Pricing Information
                    </Text>
                    <Grid gutter="md">
                      <Grid.Col span={6}>
                        <Text size="sm" c="dimmed">
                          Selling Price
                        </Text>
                        <Text size="lg" fw={700}>
                          {formatCurrency(product.list_price)}
                        </Text>
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <Text size="sm" c="dimmed">
                          Purchase Price
                        </Text>
                        <Text size="sm" fw={500}>
                          {formatCurrency(product.purchase_price)}
                        </Text>
                      </Grid.Col>
                      <Grid.Col span={12}>
                        <ProductTaxSummary
                          taxRates={productTaxRates.taxRates}
                          isLoading={productTaxRates.isLoading}
                          error={productTaxRates.error}
                        />
                      </Grid.Col>
                    </Grid>
                  </Box>

                  {/* Description */}
                  {product.description && (
                    <Box
                      p="md"
                      style={{
                        border: "1px solid var(--mantine-color-gray-3)",
                        borderRadius: "8px",
                        backgroundColor: "var(--mantine-color-gray-0)",
                      }}
                    >
                      <Text fw={500} mb="xs">
                        Description
                      </Text>
                      <Text size="sm">{product.description}</Text>
                    </Box>
                  )}

                  {/* Notes */}
                  {product.notes && (
                    <Box
                      p="md"
                      style={{
                        border: "1px solid var(--mantine-color-gray-3)",
                        borderRadius: "8px",
                        backgroundColor: "var(--mantine-color-gray-0)",
                      }}
                    >
                      <Text fw={500} mb="xs">
                        Notes
                      </Text>
                      <Text size="sm">{product.notes}</Text>
                    </Box>
                  )}

                  {/* Stock Adjustments */}
                  <Box
                    p="md"
                    style={{
                      border: "1px solid var(--mantine-color-gray-3)",
                      borderRadius: "8px",
                      backgroundColor: "var(--mantine-color-gray-0)",
                    }}
                  >
                    <Text fw={500} mb="xs">
                      Stock Adjustments
                    </Text>
                    <Text size="xs" c="dimmed" mb="md">
                      Manual stock adjustments only. Product sales are tracked
                      separately and automatically deducted from inventory.
                    </Text>

                    {adjustments.length === 0 ? (
                      <Text size="sm" c="dimmed" ta="center" py="xl">
                        No manual stock adjustments yet
                      </Text>
                    ) : (
                      <Box style={{ overflowX: "auto" }}>
                        <Table striped highlightOnHover>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>Date</Table.Th>
                              <Table.Th>Amount</Table.Th>
                              <Table.Th>Notes</Table.Th>
                              <Table.Th>Actions</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {adjustments.map((adjustment) => (
                              <Table.Tr key={adjustment.id}>
                                <Table.Td>
                                  <Text size="sm">
                                    {formatDate(adjustment.created_dt)}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  <Badge
                                    color={
                                      adjustment.adjustment > 0
                                        ? "green"
                                        : "red"
                                    }
                                  >
                                    {adjustment.adjustment > 0 ? "+" : ""}
                                    {adjustment.adjustment}
                                  </Badge>
                                </Table.Td>
                                <Table.Td>
                                  <Text size="sm">
                                    {adjustment.notes || "-"}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  <Group gap="xs">
                                    {perms?.canEdit !== false && (
                                      <ActionIcon
                                        size="sm"
                                        variant="subtle"
                                        onClick={() =>
                                          handleEditAdjustment(adjustment)
                                        }
                                      >
                                        <IconEdit size={14} />
                                      </ActionIcon>
                                    )}
                                    {perms?.canEdit !== false && (
                                      <ActionIcon
                                        size="sm"
                                        color="red"
                                        variant="subtle"
                                        onClick={() =>
                                          handleDeleteAdjustment(adjustment.id)
                                        }
                                      >
                                        <IconTrash size={14} />
                                      </ActionIcon>
                                    )}
                                  </Group>
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      </Box>
                    )}
                  </Box>

                  {/* Timestamps */}
                  <Box
                    p="md"
                    style={{
                      border: "1px solid var(--mantine-color-gray-3)",
                      borderRadius: "8px",
                      backgroundColor: "var(--mantine-color-gray-0)",
                    }}
                  >
                    <Text fw={500} mb="xs">
                      Timestamps
                    </Text>
                    <Grid gutter="md">
                      <Grid.Col span={6}>
                        <Text size="sm" c="dimmed">
                          Created
                        </Text>
                        <Text size="sm">{formatDate(product.created_dt)}</Text>
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <Text size="sm" c="dimmed">
                          Last Updated
                        </Text>
                        <Text size="sm">{formatDate(product.updated_dt)}</Text>
                      </Grid.Col>
                    </Grid>
                  </Box>
                </Stack>
              </Grid.Col>
            </Grid>
          </Box>
        </Box>
      </Modal>

      <EditProductModal
        opened={editModalOpened}
        onClose={() => {
          setEditModalOpened(false);
          // The edit form can change this product's tax rates.
          productTaxRates.refresh();
        }}
        product={product}
        readOnly={perms?.canEdit === false}
      />

      <AdjustQuantityModal
        opened={adjustQuantityModalOpened}
        onClose={() => setAdjustQuantityModalOpened(false)}
        productId={product.id}
        productName={product.name}
        onSuccess={handleRefreshAdjustments}
      />

      {selectedAdjustment && (
        <EditQuantityAdjustmentModal
          opened={editAdjustmentModalOpened}
          onClose={() => {
            setEditAdjustmentModalOpened(false);
            setSelectedAdjustment(null);
          }}
          productId={product.id}
          adjustment={selectedAdjustment}
          onSuccess={handleRefreshAdjustments}
        />
      )}

      <DeleteConfirmationModal
        isOpen={deleteModalOpened}
        onClose={() => {
          setDeleteModalOpened(false);
          setAdjustmentToDelete(null);
        }}
        onConfirm={confirmDeleteAdjustment}
        title="Delete Stock Adjustment"
        message="Are you sure you want to delete this stock adjustment? This action cannot be undone."
        isLoading={isDeleting}
      />
    </>
  );
}
